import React from 'react';
import { Link } from 'react-router-dom';

const Welcome = () => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      {/* POC Warning Banner */}
      <div className="bg-amber-100 border-l-4 border-amber-500 p-4 mb-8">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-amber-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-amber-800">Proof of Concept Version</h3>
            <div className="mt-2 text-amber-700">
              <p className="text-base">
                <strong>This is a PROOF OF CONCEPT version with FAKE DATA.</strong> The purpose is to demonstrate functionality and collect feedback. 
                None of the data shown represents real market information.
              </p>
            </div>
          </div>
        </div>
      </div>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">Welcome to Tiritix Analytics Platform</h1>
      
      <div className="prose prose-blue max-w-none">
        <p className="text-lg text-gray-600 mb-6">
          Tiritix is a comprehensive market analytics platform designed to provide in-depth insights into product sales,
          retail channel performance, and geographic market trends. This platform aims to provide Nielsen IQ and GfK level market 
          intelligence with modern visualization and hierarchical analysis capabilities.
        </p>
        
        <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Key Features</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
            <h3 className="text-xl font-semibold text-blue-800 mb-3">Top-Down Navigation</h3>
            <p className="text-gray-700 mb-3">
              Navigate seamlessly from high-level categories down to individual products. Drill down through the
              product hierarchy to discover performance patterns at every level.
            </p>
            <div className="mt-4">
              <Link to="/products/hierarchy" className="text-blue-600 hover:text-blue-800 font-medium">
                Try Product Hierarchy →
              </Link>
            </div>
          </div>
          
          <div className="bg-green-50 p-6 rounded-lg border border-green-100">
            <h3 className="text-xl font-semibold text-green-800 mb-3">Multi-Dimensional Analysis</h3>
            <p className="text-gray-700 mb-3">
              Analyze data across products, geography, retail channels, and time periods to uncover 
              complex relationships and opportunities in the market.
            </p>
            <div className="mt-4">
              <Link to="/dashboard" className="text-green-600 hover:text-green-800 font-medium">
                Explore Dashboard →
              </Link>
            </div>
          </div>
          
          <div className="bg-purple-50 p-6 rounded-lg border border-purple-100">
            <h3 className="text-xl font-semibold text-purple-800 mb-3">Geographic Insights</h3>
            <p className="text-gray-700 mb-3">
              Visualize sales performance across different regions with heat maps and detailed
              regional comparisons to identify growth opportunities.
            </p>
            <div className="mt-4">
              <Link to="/geo/heatmap" className="text-purple-600 hover:text-purple-800 font-medium">
                View World Heatmap →
              </Link>
            </div>
          </div>
          
          <div className="bg-orange-50 p-6 rounded-lg border border-orange-100">
            <h3 className="text-xl font-semibold text-orange-800 mb-3">Temporal Analysis</h3>
            <p className="text-gray-700 mb-3">
              Track performance over time with detailed time series analysis, seasonal trends,
              and year-over-year comparisons.
            </p>
            <div className="mt-4">
              <Link to="/time/series" className="text-orange-600 hover:text-orange-800 font-medium">
                Analyze Time Series →
              </Link>
            </div>
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">How to Use This POC</h2>
        
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-8">
          <ol className="list-decimal list-inside space-y-3 text-gray-700">
            <li><strong>Explore the Dashboard</strong> - Start with the overview to get a sense of the overall market performance.</li>
            <li><strong>Try Top-Down Navigation</strong> - Use the Product Hierarchy view to drill down from categories to products.</li>
            <li><strong>Check Geographic Analysis</strong> - View the world heat map to see global sales distribution.</li>
            <li><strong>Analyze Retail Channels</strong> - Explore shop performance and category distribution.</li>
            <li><strong>Review Time Patterns</strong> - Look at time series and seasonal trends to understand temporal patterns.</li>
          </ol>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Feedback Focus Areas</h2>
        
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-8">
          <p className="text-gray-700 mb-4">We're particularly interested in feedback on the following aspects:</p>
          <ul className="list-disc list-inside space-y-3 text-gray-700">
            <li>Ease of navigating through the product hierarchy</li>
            <li>Usefulness of the multi-dimensional analysis capabilities</li>
            <li>Clarity and impact of the visualizations</li>
            <li>Additional data points or metrics that would be valuable</li>
            <li>Performance and responsiveness of the interface</li>
          </ul>
        </div>
        
        <div className="mt-10 mb-6 text-center">
          <Link 
            to="/dashboard" 
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            Start Exploring →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Welcome;